<template>
  <v-card class="card--shadow">
    <v-card-title>Visit Date</v-card-title>
    <v-card-text>
      <date-generate-from @generateDate="generateDate" :isLoading="isLoading" />
      <v-row class="mt-2" v-if="startDate && endDate && dates.length > 0">
        <v-col md="4">
          <export-excel
            type="csv"
            class="export_btn"
            :fields="custom_fields"
            :fetch="getExportData"
            :name="`Visit Data Export (${formatDate(startDate)} to ${formatDate(endDate)}).csv`"
            :footer="[`Visit Start Date: ${formatDate(startDate)}`, `Visit End Date: ${formatDate(endDate)}`]"
          >
            <v-icon color="white" left>mdi-chevron-right-circle-outline</v-icon>Export
          </export-excel>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-text>
      <div v-if="this.dates.length>0" class="color-box-row mb-2">
      <div class="color-box-container">
          <div class="color-box" style="background-color: blue;"></div>
          <span>In Person</span>&nbsp;
        </div>  

      <div class="color-box-container">
        <div class="color-box" style="background-color: #1E90FF;"></div>
        <span>By Phone</span>&nbsp;
      </div>

      <div class="color-box-container">
        <div class="color-box" style="background-color: #24b014;"></div>
        <span>INNO DOT</span>&nbsp;
      </div>

      <div class="color-box-container">
        <div class="color-box" style="background-color: red;"></div>
        <span>Cannot call</span>&nbsp;
      </div>

      </div>
      <date-table-view :dates="dates" />
    </v-card-text>
  </v-card>
</template>

<script>
import DateGenerateFrom from "./UseApp/DateGenerateForm.vue";
import DateTableView from "./UseApp/DateTableView.vue";
import { generateDate } from "./helper";
import moment from "moment";
import { mapState } from "vuex";

export default {
  data: () => ({
    dates: [],
    startDate: null,
    endDate: null,
    isLoading: false,
    custom_fields: {
      Date: "date",
      PatientName : "patient_name",
      Code : "patient_code",
      Year : "patient_year",
      Mono_Poly: "patient_mono_poly",
      "Time": "calling_time",
      Status: "status",
      Type: "type",
      Duration: "duration",
      SideEffect: "side_effect",
      Action: "action",
      VolunteerCode : "volunteer_code",
      VolunteerName : "volunteer_name",
      Township: "township",
    },
  }),
  computed: {
    ...mapState("Admin", ["admin"]),
  },
  components: {
    DateGenerateFrom,
    DateTableView,
  },
  methods: {
    generateDate(data) {
      // Store the dates for export
      this.startDate = data.startDate;
      this.endDate = data.endDate;
      
      // Set loading state
      this.isLoading = true;
      this.dates = []; // Clear previous dates while loading
      
      // Use $nextTick to ensure UI updates before async call
      this.$nextTick(() => {
        this.$store
          .dispatch("DotsPatient/getVisitDate", {
            dots_patient_id: this.$route.params.id,
          })
          .then((res) => {
            this.dates = generateDate(data.startDate, data.endDate);
            this.dates = this.generateResult(this.dates, res);
            console.log(this.dates);
          })
          .catch((error) => {
            console.error("Error fetching visit dates:", error);
          })
          .finally(() => {
            // Stop loading when data is received or if there's an error
            this.isLoading = false;
          });
      });
    },
    formatDate(date) {
      return moment(date).format("YYYY-MM-DD");
    },
    secondsToHms(d) {
      // Format duration from seconds to hours, minutes, seconds (same as detail page)
      d = Number(d);
      var h = Math.floor(d / 3600);
      var m = Math.floor((d % 3600) / 60);
      var s = Math.floor((d % 3600) % 60);
      var hDisplay = h > 0 ? h + (h == 1 ? " hour, " : " hours, ") : "";
      var mDisplay = m > 0 ? m + (m == 1 ? " minute, " : " minutes, ") : "";
      var sDisplay = s > 0 ? s + (s == 1 ? " second" : " seconds") : "";
      return hDisplay + mDisplay + sDisplay;
    },
    getExportData() {
      // Export ALL dates in the selected range, not just dates with visit data
      // Get township name from admin
      const township = this.admin.township ? this.admin.township.name : '';
      
      const exportData = this.dates.map(date => {
        // If date has visit data, process it
        if (date.found && date.data) {
          const visitData = date.data || {};
          let status = '';
          let type = '';
          let duration = '';
          let calling_time = '';
          let side_effect = '';
          let action = '';
          
          if(visitData.status == 1) {
            status = 'Visited';
          } else if(visitData.status == 2) {
            status = 'Cannot call';
          } else {
            status = 'Pending';
          }

          if (visitData.type == 1) {
            type = 'by-phone';
          } else if (visitData.type == 2) {
            type = 'in-person';
          } else if (visitData.type == 3) {
            type = 'by-inno-dot';
          }
          
          // Duration: only shown when type == 1 (by-phone) like in detail page
          if (visitData.type == 1 && visitData.duration) {
            duration = this.secondsToHms(visitData.duration);
          }
          
          // Calling time: only shown when type != 3 (like in detail page)
          if (visitData.type != 3 && visitData.calling_time) {
            calling_time = visitData.calling_time;
          }

          if(visitData.low_side_effect) {
            side_effect = visitData.low_side_effect;
          }
          if (visitData.high_side_effect) {
            action = visitData.high_side_effect;
          }
          
          // Format monopoly value: 1=No, 2=Yes, 3=SG, 4=MGY
          const monopolyValue = visitData.dots_patient?.patient?.mdrtb_monopoly;
          let patient_mono_poly = '';
          if (monopolyValue == 1) {
            patient_mono_poly = 'No';
          } else if (monopolyValue == 2) {
            patient_mono_poly = 'Yes';
          } else if (monopolyValue == 3) {
            patient_mono_poly = 'SG';
          } else if (monopolyValue == 4) {
            patient_mono_poly = 'MGY';
          }
          
          return {
            date: date.date,
            status: status,
            type: type,
            duration: duration,
            calling_time: calling_time,
            side_effect: side_effect,
            action: action,
            patient_code: visitData.dots_patient?.patient?.mdrtb_reg_no || '',
            patient_year: visitData.dots_patient?.patient?.mdrtb_reg_year || '',
            patient_mono_poly: patient_mono_poly,
            patient_name: visitData.dots_patient?.patient?.name || '',
            volunteer_code: visitData.volunteer?.code || '',
            volunteer_name: visitData.volunteer?.name || '',
            township: township,
          };
        } else {
          // Date has no visit data - return empty values
          return {
            date: date.date,
            status: '',
            type: '',
            duration: '',
            calling_time: '',
            side_effect: '',
            action: '',
            volunteer_code: '',
            volunteer_name: '',
            patient_code: '',
            patient_year: '',
            patient_mono_poly: '',
            patient_name: '',
            township: township,
          };
        }
      });
      
      return exportData;
    },
    generateResult(dates, res) {
      dates = dates.map((date) => {
        res.forEach((foundDate) => {
          if (foundDate.date == date.date) {
            date.found = true;
            date.data = foundDate;
          }
        });
        return date;
      });

      return dates;
    },
  },
};
</script>

<style>
/* legend */
.color-box-container {
  display: flex;
  align-items: center;
}

.color-box-row {
  display: flex;
}


.color-box {
  width: 16px; 
  height: 16px;
  border-radius: 3px;
  margin-right: 2px; 
  
  border: 1px solid #000; 
}

.color-box-container span {
  font-size: 14px; 
  color: #333;
}

.export_btn {
  text-align: center;
  background: #ff4081;
  border-radius: 3px;
  color: white !important;
  letter-spacing: 1px;
  text-transform: uppercase;
  width: 150px;
  padding: 6px 8px;
  margin-bottom: 1px;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4) !important;
}

</style>
